import { useLanguage } from '@/i18n/LanguageContext';
import { Playlist } from '@/data/playlists';
import { rooms } from '@/data/rooms';
import { Play, Clock, ChevronRight, Circle } from 'lucide-react';

interface PlaylistCardProps {
  playlist: Playlist;
  onStart: (id: string) => void;
  isActive?: boolean;
}

const moodAccents: Record<string, string> = {
  calm: 'border-l-emerald-400/25 hover:shadow-emerald-500/5',
  intense: 'border-l-red-400/25 hover:shadow-red-500/5',
  reflective: 'border-l-blue-400/25 hover:shadow-blue-500/5',
  creative: 'border-l-amber-400/25 hover:shadow-amber-500/5',
  dark: 'border-l-purple-400/25 hover:shadow-purple-500/5',
};

const PlaylistCard = ({ playlist, onStart, isActive }: PlaylistCardProps) => {
  const { t } = useLanguage();

  const roomTitles = playlist.rooms
    .map(id => rooms.find(r => r.id === id)?.title || id)
    .slice(0, 3);

  return (
    <button
      onClick={() => onStart(playlist.id)}
      className={`w-full text-left p-5 rounded-xl border border-border/10 border-l-2 bg-card/30 hover:bg-card/60 transition-all duration-500 hover:-translate-y-1 hover:shadow-lg group ${
        moodAccents[playlist.mood] || ''
      } ${isActive ? 'border-foreground/20 bg-card/50' : ''}`}
    >
      <div className="flex items-start justify-between mb-4">
        <Circle className="w-3 h-3 text-muted-foreground/20 group-hover:text-muted-foreground/40 transition-colors" />
        <div className="flex items-center gap-1 text-[9px] text-muted-foreground/25 tracking-widest uppercase">
          <Clock className="w-2.5 h-2.5" />
          <span>{playlist.estimatedMinutes}m</span>
        </div>
      </div>

      <h3 className="text-sm font-light tracking-tight mb-1.5 text-foreground/75 group-hover:text-foreground transition-colors duration-500">
        {t(playlist.titleKey)}
      </h3>
      <p className="text-xs text-muted-foreground/30 mb-4 line-clamp-2 leading-relaxed group-hover:text-muted-foreground/50 transition-colors duration-500">
        {t(playlist.descriptionKey)}
      </p>

      <div className="flex items-center gap-1 text-[9px] text-muted-foreground/20 mb-4 tracking-wide">
        {roomTitles.map((title, i) => (
          <span key={i} className="flex items-center gap-1">
            {i > 0 && <ChevronRight className="w-2 h-2" />}
            <span className="truncate max-w-[70px]">{title}</span>
          </span>
        ))}
        {playlist.rooms.length > 3 && <span className="text-muted-foreground/15">+{playlist.rooms.length - 3}</span>}
      </div>

      <div className="flex items-center gap-2 text-xs text-foreground/40 group-hover:text-foreground/70 transition-colors duration-500">
        <Play className="w-3 h-3" />
        <span className="tracking-wide">{isActive ? (t('continue') || 'Continue') : (t('start') || 'Start')}</span>
      </div>
    </button>
  );
};

export default PlaylistCard;
